import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { ttStatsAPI } from '../services/api';
import { Player, Club } from '../types/api.types';
import UnifiedFooter from '../components/UnifiedFooter';
import { useOptimizedLogo } from '../hooks/useOptimizedLogo';
import MonthlyRankings from '../components/MonthlyRankings';
import TournamentsSection from '../components/TournamentsSection';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { homePageTranslations } from '../translations/homePage';
import HeroSection from '../components/home/HeroSection';
import SearchBar from '../components/home/SearchBar';
import SearchResults from '../components/home/SearchResults';
import FavoritesDropdown from '../components/home/FavoritesDropdown';
import NoResults from '../components/home/NoResults';
import LoadingResults from '../components/home/LoadingResults';
import ScrollIndicator from '../components/home/ScrollIndicator';
import AnimatedStats from '../components/AnimatedStats';

const HomePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ players: Player[]; clubs: Club[] }>({ players: [], clubs: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchAnimationComplete] = useState(true); // Always true now since HeroSection handles its own animation
  const hasShownAuthNotification = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();

  // Validate language parameter - if not valid, redirect to 404
  useEffect(() => {
    const validLangs = ['en', 'fr', 'de', 'it'];
    if (lang && !validLangs.includes(lang)) {
      navigate(`/en/404`, { replace: true });
    }
  }, [lang, navigate]);

  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const logoSrc = useOptimizedLogo();
  const { isFavorite, favorites } = useAuth();

  const t = homePageTranslations[currentLang] || homePageTranslations.en;

  const performSearch = useCallback(async () => {
    setIsSearching(true);
    try {
      const [playerData, clubData] = await Promise.all([
        ttStatsAPI.searchPlayers(searchQuery),
        ttStatsAPI.searchClubs(searchQuery)
      ]);

      // Sort players: favorites first, then by name
      const sortedPlayers = (playerData.players || []).sort((a, b) => {
        const aIsFav = isFavorite(a.url || '');
        const bIsFav = isFavorite(b.url || '');
        if (aIsFav && !bIsFav) return -1;
        if (!aIsFav && bIsFav) return 1;
        return 0;
      });

      setSearchResults({
        players: sortedPlayers,
        clubs: clubData.clubs || []
      });
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({ players: [], clubs: [] });
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, isFavorite]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else {
        setSearchResults({ players: [], clubs: [] });
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, performSearch]);

  // Check for auth errors in URL hash
  useEffect(() => {
    // Skip if we've already shown a notification
    if (hasShownAuthNotification.current) return;

    const hash = window.location.hash;

    if (hash.includes('error=')) {
      hasShownAuthNotification.current = true;

      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');

      let message = t.auth.errorMessage;
      let title = t.auth.errorTitle;

      if (errorCode === 'otp_expired' || error === 'access_denied') {
        title = t.auth.linkExpired;
        message = t.auth.linkExpiredMessage;
      } else if (errorDescription) {
        message = errorDescription.replace(/\+/g, ' ');
      }

      notifications.show({
        title,
        message,
        color: 'red',
        autoClose: 7000,
      });

      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Check for successful auth - but DON'T clean the URL immediately
    // Supabase needs the tokens to complete authentication
    if (hash.includes('access_token=')) {
      hasShownAuthNotification.current = true;

      notifications.show({
        title: t.auth.welcome,
        message: t.auth.welcomeMessage,
        color: 'green',
        autoClose: 3000,
      });

      // Clean the URL after Supabase has processed the tokens
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang]);

  const handlePlayerSelect = (player: Player) => {
    if (player.url) {
      navigate(`/${currentLang}/player/${encodeURIComponent(player.url)}`);
    }
  };

  const handleClubSelect = (clubId: string) => {
    navigate(`/${currentLang}/club/${clubId}`);
  };

  const handleFavoriteClick = (link: string) => {
    navigate(`/${currentLang}/player/${encodeURIComponent(link)}`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      performSearch();
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col relative transition-colors duration-300">
      {/* SEO Head - Dynamic meta tags */}
      <SEOHead lang={currentLang} />

      {/* Main Content - Google Style - Full Screen */}
      <div className="flex flex-col items-center justify-center px-4 -mt-16" style={{ minHeight: '100dvh' }}>
        {/* Content Container */}
        <div className="w-full max-w-2xl mx-auto">
          {/* Hero Section with Logo, Title, Tagline */}
          <HeroSection logoSrc={logoSrc} currentLang={currentLang} />

          {/* Search Bar - delayed appearance */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.7, ease: [0.4, 0, 0.2, 1] }}
          >
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isSearching={isSearching}
              isSearchFocused={isSearchFocused}
              setIsSearchFocused={setIsSearchFocused}
              onSubmit={handleSearch}
              placeholder={t.searchPlaceholder}
              logoSrc={logoSrc}
              searchInputRef={searchInputRef}
            >
          {/* No Results State */}
          {showResults && searchResults.players.length === 0 && searchResults.clubs.length === 0 && !isSearching && searchQuery.length >= 2 && (
            <NoResults t={{ noResultsFound: t.noResultsFound, tryDifferentSearch: t.tryDifferentSearch }} />
          )}

          {/* Favorites or Search Results Dropdown */}
          <AnimatePresence>
            {searchAnimationComplete && ((isSearchFocused && searchQuery.length === 0 && favorites.length > 0) || (showResults && (searchResults.players.length > 0 || searchResults.clubs.length > 0))) && (
              <>
                {/* Show Favorites when search is focused but empty */}
                {isSearchFocused && searchQuery.length === 0 && favorites.length > 0 ? (
                  <FavoritesDropdown
                    favorites={favorites}
                    onFavoriteClick={handleFavoriteClick}
                    t={{ yourFavorites: t.yourFavorites }}
                  />
                ) : (
                  /* Show Search Results */
                  <SearchResults
                    players={searchResults.players}
                    clubs={searchResults.clubs}
                    currentLang={currentLang}
                    isFavorite={isFavorite}
                    onPlayerSelect={handlePlayerSelect}
                    onClubSelect={handleClubSelect}
                    t={{ players: t.players, clubs: t.clubs }}
                  />
                )}
              </>
            )}
          </AnimatePresence>

          {/* Loading State - Sleek skeleton loader */}
          {isSearching && <LoadingResults />}
        </SearchBar>
          </motion.div>

        {/* Animated Stats - delayed appearance */}
        <AnimatedStats lang={currentLang} />

        {/* Scroll Indicator - delayed appearance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 2.8 }}
        >
          <ScrollIndicator />
        </motion.div>
        </div>
      </div>

      {/* Tournaments Section - Combined Partnered & Upcoming */}
      <TournamentsSection />

      {/* Monthly Rankings Section */}
      <MonthlyRankings />

      {/* Unified Footer with App Showcase */}
      <UnifiedFooter variant="showcase" lang={currentLang} />
    </div>
  );
};

export default HomePage;
